import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { TeeOnSlotDto, TeeOnStatusDto, TeeOnTeeSheetDto } from '@jordan/types';
import { Alert, Badge, Breadcrumb, Button, DataTable, PageHeader, SelectMenu, Spinner, type DataTableColumn } from '@jordan/ui';
import { AppChrome } from '../../../components/AppChrome';
import { ApiError, apiGet } from '../../../lib/api';
import { golfSidebar } from './golfSidebar';

const todayIso = () => new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD in local time

// Normalize a name for matching the configured member against tee-sheet player names.
const normName = (s: string) => s.trim().toLowerCase().replace(/\s+/g, ' ');

// Availability filter: value = the minimum number of open spots a slot must have.
const OPEN_FILTERS = [
  { value: '0', label: 'All' },
  { value: '1', label: 'Open 1 Player' },
  { value: '2', label: 'Open 2+ players' },
  { value: '3', label: 'Open 3+ players' },
  { value: '4', label: 'Open 4 Players' },
] as const;

const formatDate = (ymd: string) =>
  new Date(`${ymd}T00:00:00`).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });

const shiftDate = (ymd: string, days: number) => {
  const d = new Date(`${ymd}T00:00:00`);
  d.setDate(d.getDate() + days);
  return d.toLocaleDateString('en-CA');
};

export function GolfTeeTimesPage() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [status, setStatus] = useState<TeeOnStatusDto | null>(null);
  const [date, setDate] = useState<string>(todayIso());
  const [sheet, setSheet] = useState<TeeOnTeeSheetDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [openFilter, setOpenFilter] = useState<string>('0');

  useEffect(() => {
    apiGet<TeeOnStatusDto>('/api/health/golf/teeon/status')
      .then(setStatus)
      .catch(() => setErr('Failed to load Tee-On status.'));
  }, []);

  const loadSheet = useCallback((d: string) => {
    setLoading(true);
    setErr(null);
    apiGet<TeeOnTeeSheetDto>(`/api/health/golf/teeon/tee-sheet?date=${encodeURIComponent(d)}`)
      .then((s) => {
        setSheet(s);
        setDate(s.date);
      })
      .catch((e: unknown) => {
        const body = e instanceof ApiError ? (e.body as { message?: string } | null) : null;
        setErr(body?.message ?? 'Could not load the tee sheet.');
        setSheet(null);
      })
      .finally(() => setLoading(false));
  }, []);

  // Load the sheet once we know Tee-On is configured.
  useEffect(() => {
    if (status?.configured) loadSheet(date);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status?.configured]);

  const dates = sheet?.availableDates ?? [];
  const idx = dates.indexOf(date);
  const prevDate = idx > 0 ? dates[idx - 1] : dates.length ? null : shiftDate(date, -1);
  const nextDate = idx >= 0 && idx < dates.length - 1 ? dates[idx + 1] : dates.length ? null : shiftDate(date, 1);

  const bookingEnabled = status?.bookingEnabled ?? false;
  const memberName = status?.memberName ?? '';

  // A slot is "mine" when the configured member name matches one of its players.
  const isMine = useCallback(
    (s: TeeOnSlotDto) => !!memberName && s.players.some((p) => normName(p) === normName(memberName)),
    [memberName],
  );

  // A slot is in the past when its start (the sheet's date + slot time) is before now, compared in
  // the course's timezone (Eastern). Times/dates are zero-padded strings, so lexical compare works.
  const nowEtDate = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Toronto' }).format(new Date());
  const nowEtTime = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'America/Toronto',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(new Date());
  const isPast = (s: TeeOnSlotDto) => {
    if (!sheet) return false;
    if (sheet.date < nowEtDate) return true;
    if (sheet.date > nowEtDate) return false;
    return s.time < nowEtTime;
  };

  // Apply the availability filter (minimum open spots) before handing rows to the table.
  const slots = useMemo(() => {
    const min = Number(openFilter);
    const all = sheet?.slots ?? [];
    return min > 0 ? all.filter((s) => s.openSpots >= min) : all;
  }, [sheet, openFilter]);

  const filterToolbar = (
    <div style={{ minWidth: 210 }}>
      <SelectMenu
        icon="ph-funnel"
        value={openFilter}
        onChange={setOpenFilter}
        options={OPEN_FILTERS.map((o) => ({ value: o.value, label: o.label }))}
      />
    </div>
  );

  const columns = useMemo<DataTableColumn<TeeOnSlotDto>[]>(
    () => [
      {
        key: 'label',
        header: 'Time',
        sortable: true,
        // Sort chronologically by the 24-hour time, not the "h:mm am/pm" label (which would put
        // "1:00 pm" before "7:00 am" under numeric collation).
        sortValue: (s) => s.time,
        render: (s) => <span className="fw-semibold">{s.label}</span>,
      },
      {
        key: 'players',
        header: 'Players',
        render: (s) => (s.players.length ? s.players.join(', ') : <span className="text-muted fst-italic">Open</span>),
      },
      {
        key: 'openSpots',
        header: 'Open',
        align: 'center',
        sortable: true,
        render: (s) =>
          s.openSpots > 0 ? (
            <Badge variant="success">
              {s.openSpots} of {s.maxPlayers}
            </Badge>
          ) : (
            <Badge variant="light">Full</Badge>
          ),
      },
      { key: 'holes', header: 'Holes', align: 'center', render: (s) => s.holes ?? '—' },
      {
        key: 'actions',
        header: '',
        align: 'end',
        render: (s) => (
          <Button
            variant={s.bookable ? 'primary' : 'light'}
            size="sm"
            icon="ph-golf"
            disabled={!s.bookable || !bookingEnabled}
            title={
              !s.bookable
                ? 'This time is full.'
                : !bookingEnabled
                  ? "Booking from Jordan isn't enabled yet."
                  : `Book ${s.label}`
            }
            onClick={() => {
              /* booking is not wired up yet */
            }}
          >
            Book
          </Button>
        ),
      },
    ],
    [bookingEnabled],
  );

  return (
    <AppChrome secondarySidebar={golfSidebar(navigate, pathname)}>
      <PageHeader
        title={
          <>
            Golf - <span className="fw-normal">Tee Times</span>
          </>
        }
        breadcrumb={<Breadcrumb items={[{ icon: 'ph-house', href: '/' }, { label: 'Golf' }, { label: 'Tee Times' }]} />}
      />

      <div className="content">
        {status && !status.configured && (
          <Alert variant="info" icon="ph-info">
            Tee-On isn&apos;t connected. Add <code>TEEON_USERNAME</code> and <code>TEEON_PASSWORD</code> to the server{' '}
            <code>.env</code> to view tee sheets.
          </Alert>
        )}

        {err && (
          <Alert variant="danger" icon="ph-warning" dismissible>
            {err}
          </Alert>
        )}

        {status?.configured && (
          <div className="card">
            <div className="card-header d-flex flex-wrap align-items-center gap-2">
              <h5 className="mb-0 me-auto">
                {status.defaultCourseName}
                {sheet && <span className="text-muted fw-normal ms-2">— {formatDate(sheet.date)}</span>}
              </h5>
              <Button
                variant="light"
                icon="ph-caret-left"
                disabled={loading || !prevDate}
                onClick={() => prevDate && loadSheet(prevDate)}
              >
                Prev
              </Button>
              <input
                type="date"
                className="form-control w-auto"
                value={date}
                min={dates[0]}
                max={dates[dates.length - 1]}
                disabled={loading}
                onChange={(e) => e.target.value && loadSheet(e.target.value)}
              />
              <Button
                variant="light"
                icon="ph-caret-right"
                disabled={loading || !nextDate}
                onClick={() => nextDate && loadSheet(nextDate)}
              >
                Next
              </Button>
              <Button variant="light" icon="ph-arrows-clockwise" disabled={loading} onClick={() => loadSheet(date)}>
                Refresh
              </Button>
            </div>
            <div className="card-body">
              {loading ? (
                <div className="text-center py-5">
                  <Spinner />
                  {!sheet && <div className="text-muted mt-2">Signing in to Tee-On… this first load can take a moment.</div>}
                </div>
              ) : (
                <>
                  {memberName && (sheet?.slots ?? []).some(isMine) && (
                    <div className="text-muted small mb-2">
                      <span className="badge bg-warning text-dark me-1">&nbsp;</span>
                      Highlighted rows are where <span className="fw-semibold">{memberName}</span> is booked.
                    </div>
                  )}
                  <DataTable
                    columns={columns}
                    rows={slots}
                    getRowKey={(s) => s.time}
                    searchable
                    toolbar={filterToolbar}
                    rowClassName={(s) => (isPast(s) ? 'opacity-50' : isMine(s) ? 'table-warning' : undefined)}
                    pageSize={status?.teeSheetPageSize ?? 0}
                    initialSort={{ key: 'label', dir: 'asc' }}
                    emptyMessage="No tee times match this filter."
                  />
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </AppChrome>
  );
}
